import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  MapPin,
  Globe,
  Phone,
  Mail,
  Building2,
  Ruler,
  Ship,
  Award,
  ExternalLink,
  Plus,
  Trash2,
  Upload,
  FileText,
  MessageSquare,
  Users,
  Paperclip,
  Clock,
  ChevronDown,
  MoreHorizontal,
  User,
  FileText as Description,
  Linkedin,
  Facebook,
  Twitter,
  Calendar,
  DollarSign,
  Briefcase,
  Building,
  Tag,
  Printer,
  Hash,
  Star,
  X,
  PanelRightOpen,
  PanelRightClose,
  Edit3,
  Save,
} from 'lucide-react'
import {
  useLead,
  useUpdateLeadStatus,
  useUpdateLead,
  useDeleteLead,
  useLeadNotes,
  useAddLeadNote,
  useDeleteLeadNote,
  useLeadActivities,
  useAddLeadActivity,
  useLeadContacts,
  useAddLeadContact,
  useDeleteLeadContact,
  useLeadAttachments,
  useUploadLeadAttachment,
  useDeleteLeadAttachment,
  useUploadLeadImage,
  useToggleLongTerm,
} from '@/hooks/useLeads'
import {
  useIsShortlisted,
  useAddToShortlist,
  useRemoveFromShortlist,
  useUpdateShortlistItem,
  SHORTLIST_STATUS_LABELS,
  SHORTLIST_STATUS_COLORS,
  type ShortlistStatus,
} from '@/hooks/useShortlist'
import { useAuth } from '@/hooks/useAuth'
import type { LeadStatus, LeadActivity } from '@/types/lead'
import { cn } from '@/lib/utils'

const STATUS_OPTIONS: { value: LeadStatus; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: '#6366f1' },
  { value: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { value: 'qualified', label: 'Qualified', color: '#06b6d4' },
  { value: 'proposal', label: 'Proposal', color: '#f59e0b' },
  { value: 'negotiation', label: 'Negotiation', color: '#f97316' },
  { value: 'won', label: 'Won', color: '#22c55e' },
  { value: 'lost', label: 'Lost', color: '#ef4444' },
]

const ACTIVITY_ICONS: Record<LeadActivity['type'], typeof Phone> = {
  call: Phone,
  email: Mail,
  meeting: Users,
  note: MessageSquare,
  status_change: Clock,
  other: MoreHorizontal,
}

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const currentUserEmail = user?.email || 'Unknown'

  // Build back URL - check if coming from shortlist
  const fromShortlist = searchParams.get('from') === 'shortlist'
  const backUrl = fromShortlist
    ? '/admin/shortlist'
    : `/admin/leads${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  const { data: lead, isLoading } = useLead(id)
  const { data: notes = [] } = useLeadNotes(id)
  const { data: activities = [] } = useLeadActivities(id)
  const { data: contacts = [] } = useLeadContacts(id)
  const { data: attachments = [] } = useLeadAttachments(id)

  // Shortlist
  const { data: shortlistItem } = useIsShortlisted(id)
  const addToShortlist = useAddToShortlist()
  const removeFromShortlist = useRemoveFromShortlist()
  const updateShortlistItem = useUpdateShortlistItem()

  // Long Term
  const toggleLongTerm = useToggleLongTerm()

  const updateStatus = useUpdateLeadStatus()
  const updateLead = useUpdateLead()
  const deleteLead = useDeleteLead()
  const addNote = useAddLeadNote()
  const deleteNote = useDeleteLeadNote()
  const addActivity = useAddLeadActivity()
  const addContact = useAddLeadContact()
  const deleteContact = useDeleteLeadContact()
  const uploadAttachment = useUploadLeadAttachment()
  const deleteAttachment = useDeleteLeadAttachment()
  const uploadImage = useUploadLeadImage()

  const [activeTab, setActiveTab] = useState<'notes' | 'activities' | 'contacts' | 'files'>('notes')
  const [newNote, setNewNote] = useState('')
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [descriptionLang, setDescriptionLang] = useState<'en' | 'tr'>('tr')
  const [showAddContact, setShowAddContact] = useState(false)
  const [showAddActivity, setShowAddActivity] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [showShortlistMenu, setShowShortlistMenu] = useState(false)
  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    is_primary: false,
  })
  const [newActivity, setNewActivity] = useState({
    type: 'note' as LeadActivity['type'],
    title: '',
    description: '',
  })

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    website: '',
    phone: '',
    email: '',
    fax: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    description_en: '',
    description_tr: '',
    year_founded: '',
    employees: '',
    revenue: '',
    industry: '',
    linkedin_url: '',
    facebook_url: '',
    twitter_url: '',
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)

  // Initialize edit form when entering edit mode
  useEffect(() => {
    if (isEditMode && lead) {
      setEditForm({
        name: lead.name || '',
        website: lead.website || '',
        phone: lead.phone || '',
        email: lead.email || '',
        fax: lead.fax || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        zip_code: lead.zip_code || '',
        country: lead.country || '',
        description_en: lead.description_en || '',
        description_tr: lead.description_tr || '',
        year_founded: lead.year_founded?.toString() || '',
        employees: lead.employees?.toString() || '',
        revenue: lead.revenue?.toString() || '',
        industry: lead.industry || '',
        linkedin_url: lead.linkedin_url || '',
        facebook_url: lead.facebook_url || '',
        twitter_url: lead.twitter_url || '',
      })
    }
  }, [isEditMode, lead])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-secondary-900">Lead not found</h2>
        <Link to="/admin/leads" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to leads
        </Link>
      </div>
    )
  }

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === lead.status)
  const isShortlisted = !!shortlistItem

  const handleStatusChange = (status: LeadStatus) => {
    updateStatus.mutate({ id: lead.id, status })
    setShowStatusDropdown(false)
    addActivity.mutate({
      lead_id: lead.id,
      type: 'status_change',
      title: `Status changed to ${STATUS_OPTIONS.find((s) => s.value === status)?.label}`,
      description: null,
      metadata: { from: lead.status, to: status },
      created_by: currentUserEmail,
    })
  }

  const handleToggleShortlist = async () => {
    if (isShortlisted) {
      await removeFromShortlist.mutateAsync(lead.id)
    } else {
      await addToShortlist.mutateAsync({ lead_id: lead.id })
    }
  }

  const handleShortlistStatusChange = async (status: ShortlistStatus) => {
    if (shortlistItem) {
      await updateShortlistItem.mutateAsync({ id: shortlistItem.id, status })
    }
    setShowShortlistMenu(false)
  }

  const handleDeleteLead = async () => {
    await deleteLead.mutateAsync(lead.id)
    navigate('/admin/leads')
  }

  const handleSaveEdit = async () => {
    await updateLead.mutateAsync({
      id: lead.id,
      name: editForm.name,
      website: editForm.website || undefined,
      phone: editForm.phone || undefined,
      email: editForm.email || undefined,
      fax: editForm.fax || undefined,
      address: editForm.address || undefined,
      city: editForm.city || undefined,
      state: editForm.state || undefined,
      zip_code: editForm.zip_code || undefined,
      country: editForm.country || undefined,
      description_en: editForm.description_en || undefined,
      description_tr: editForm.description_tr || undefined,
      year_founded: editForm.year_founded ? parseInt(editForm.year_founded) : undefined,
      employees: editForm.employees ? parseInt(editForm.employees) : undefined,
      revenue: editForm.revenue ? parseFloat(editForm.revenue) : undefined,
      industry: editForm.industry || undefined,
      linkedin_url: editForm.linkedin_url || undefined,
      facebook_url: editForm.facebook_url || undefined,
      twitter_url: editForm.twitter_url || undefined,
    })
    setIsEditMode(false)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
  }

  const handleAddNote = () => {
    if (!newNote.trim()) return
    addNote.mutate({ lead_id: lead.id, content: newNote, created_by: currentUserEmail })
    setNewNote('')
  }

  const handleAddContact = () => {
    if (!newContact.name.trim()) return
    addContact.mutate({ lead_id: lead.id, ...newContact, notes: null })
    setNewContact({ name: '', title: '', email: '', phone: '', is_primary: false })
    setShowAddContact(false)
  }

  const handleAddActivity = () => {
    if (!newActivity.title.trim()) return
    addActivity.mutate({
      lead_id: lead.id,
      type: newActivity.type,
      title: newActivity.title,
      description: newActivity.description || null,
      metadata: null,
      created_by: currentUserEmail,
    })
    setNewActivity({ type: 'note', title: '', description: '' })
    setShowAddActivity(false)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) uploadAttachment.mutate({ leadId: lead.id, file })
    e.target.value = ''
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) uploadImage.mutate({ leadId: lead.id, file })
    e.target.value = ''
  }

  // Format revenue
  const formatRevenue = (revenue: number | null) => {
    if (!revenue) return null
    if (revenue >= 1000000000) return `$${(revenue / 1000000000).toFixed(1)}B`
    if (revenue >= 1000000) return `$${(revenue / 1000000).toFixed(1)}M`
    if (revenue >= 1000) return `$${(revenue / 1000).toFixed(0)}K`
    return `$${revenue}`
  }

  return (
    <div className="min-h-screen bg-secondary-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate(backUrl)}
              className="p-1.5 sm:p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-xl font-bold text-secondary-900 truncate">{lead.name}</h1>
                {lead.is_headquarter && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex-shrink-0">
                    HQ
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-secondary-500 mt-0.5">
                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">
                  {[lead.city, lead.state, lead.country].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>

            {/* Shortlist Button */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => isShortlisted ? setShowShortlistMenu(!showShortlistMenu) : handleToggleShortlist()}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isShortlisted
                    ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    : 'bg-purple-500 text-white hover:bg-purple-600 shadow-sm'
                )}
              >
                <Star className={cn('w-4 h-4', isShortlisted && 'fill-purple-500')} />
                <span className="hidden sm:inline">
                  {isShortlisted ? 'Shortlisted' : 'Add to Shortlist'}
                </span>
                {isShortlisted && <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {showShortlistMenu && isShortlisted && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowShortlistMenu(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-purple-200 py-1 z-20">
                    <div className="px-3 py-1.5 text-xs font-medium text-secondary-500 uppercase">
                      Status
                    </div>
                    {(Object.keys(SHORTLIST_STATUS_LABELS) as ShortlistStatus[]).map((status) => (
                      <button
                        key={status}
                        onClick={() => handleShortlistStatusChange(status)}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary-50"
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: SHORTLIST_STATUS_COLORS[status] }}
                        />
                        {SHORTLIST_STATUS_LABELS[status]}
                      </button>
                    ))}
                    <div className="border-t border-secondary-200 mt-1 pt-1">
                      <button
                        onClick={handleToggleShortlist}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove from Shortlist
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Long Term Toggle */}
            <button
              onClick={() => toggleLongTerm.mutate({ id: lead.id, isLongTerm: !lead.long_term })}
              disabled={toggleLongTerm.isPending}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                lead.long_term
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              )}
            >
              <Clock className={cn('w-4 h-4', lead.long_term && 'text-amber-600')} />
              <span className="hidden sm:inline">
                {lead.long_term ? 'Long Term' : 'Mark Long Term'}
              </span>
            </button>

            {/* Status Dropdown - only visible when shortlisted */}
            {isShortlisted && (
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-white text-sm sm:text-base font-medium transition-colors"
                  style={{ backgroundColor: currentStatus?.color }}
                >
                  {currentStatus?.label}
                  <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>

                {showStatusDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-secondary-200 py-1 z-20">
                      {STATUS_OPTIONS.map((status) => (
                        <button
                          key={status.value}
                          onClick={() => handleStatusChange(status.value)}
                          className={cn(
                            'w-full text-left px-4 py-2 text-sm hover:bg-secondary-50 flex items-center gap-2',
                            lead.status === status.value && 'bg-secondary-50'
                          )}
                        >
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Edit/Save/Cancel Buttons */}
            {isEditMode ? (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 hover:bg-secondary-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={updateLead.isPending}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {updateLead.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditMode(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}

            {/* Delete Button - disabled if shortlisted or in edit mode */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isShortlisted || isEditMode}
              title={isShortlisted ? 'Remove from shortlist first to delete' : 'Delete company'}
              className={cn(
                'p-2 rounded-lg transition-colors flex-shrink-0',
                isShortlisted || isEditMode
                  ? 'bg-secondary-100 text-secondary-300 cursor-not-allowed'
                  : 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600'
              )}
            >
              <Trash2 className="w-5 h-5" />
            </button>

            {/* Panel Toggle Button - only visible when shortlisted */}
            {isShortlisted && (
              <button
                onClick={() => setIsPanelOpen(!isPanelOpen)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all shadow-sm',
                  isPanelOpen
                    ? 'bg-green-600 text-white shadow-green-200'
                    : 'bg-green-500 text-white hover:bg-green-600 shadow-green-200 animate-pulse hover:animate-none'
                )}
              >
                {isPanelOpen ? (
                  <PanelRightClose className="w-5 h-5" />
                ) : (
                  <PanelRightOpen className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Actions</span>
                {(notes.length + activities.length + contacts.length + attachments.length) > 0 && (
                  <span className="bg-white text-green-600 text-xs px-1.5 py-0.5 rounded-full font-bold">
                    {notes.length + activities.length + contacts.length + attachments.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex">
        {/* Main Content */}
        <div className={cn(
          'flex-1 transition-all duration-300',
          isPanelOpen ? 'mr-0 lg:mr-96' : 'mr-0'
        )}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Hero Section with Image */}
              {lead.image_url ? (
                <div className="bg-white rounded-xl border border-secondary-200 overflow-hidden">
                  <div className="h-48 sm:h-64 md:h-80 bg-secondary-100 relative group">
                    <img src={lead.image_url} alt={lead.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                    >
                      <Upload className="w-6 h-6 mr-2" />
                      Change Image
                    </button>
                  </div>
                </div>
              ) : null}

              {/* About/Description - at the top */}
              {(lead.description_en || lead.description_tr || isEditMode) && (
                <div className="bg-white rounded-xl border border-secondary-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-secondary-100 bg-secondary-50/50">
                    <h3 className="font-semibold text-secondary-900 flex items-center gap-2">
                      <Description className="w-5 h-5 text-secondary-500" />
                      About
                    </h3>
                    <div className="flex items-center gap-1 p-0.5 bg-secondary-100 rounded-lg">
                      <button
                        onClick={() => setDescriptionLang('tr')}
                        className={cn(
                          'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                          descriptionLang === 'tr' ? 'bg-white text-secondary-900 shadow-sm' : 'text-secondary-500 hover:text-secondary-700'
                        )}
                      >
                        TR
                      </button>
                      <button
                        onClick={() => setDescriptionLang('en')}
                        className={cn(
                          'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                          descriptionLang === 'en' ? 'bg-white text-secondary-900 shadow-sm' : 'text-secondary-500 hover:text-secondary-700'
                        )}
                      >
                        EN
                      </button>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    {isEditMode ? (
                      <textarea
                        value={descriptionLang === 'en' ? editForm.description_en : editForm.description_tr}
                        onChange={(e) => setEditForm({
                          ...editForm,
                          [descriptionLang === 'en' ? 'description_en' : 'description_tr']: e.target.value
                        })}
                        rows={6}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        placeholder={descriptionLang === 'en' ? 'Description in English...' : 'Türkçe açıklama...'}
                      />
                    ) : (
                      <div
                        className="text-secondary-700 leading-relaxed prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: descriptionLang === 'en' && lead.description_en
                            ? lead.description_en
                            : lead.description_tr || lead.description_en || ''
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Quick Info - moved from sidebar to under image */}
              <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-secondary-900">Quick Info</h3>
                  {!lead.image_url && (
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Photo
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {lead.type && (
                    <div className="text-center p-3 bg-secondary-50 rounded-lg">
                      <div className="text-xs text-secondary-500 mb-1">Type</div>
                      <div className="text-sm font-medium text-secondary-900 capitalize">{lead.type}</div>
                    </div>
                  )}
                  {lead.market_share && (
                    <div className="text-center p-3 bg-secondary-50 rounded-lg">
                      <div className="text-xs text-secondary-500 mb-1">Market Share</div>
                      <div className="text-sm font-medium text-secondary-900">{lead.market_share.toFixed(1)}%</div>
                    </div>
                  )}
                  {lead.rank && (
                    <div className="text-center p-3 bg-secondary-50 rounded-lg">
                      <div className="text-xs text-secondary-500 mb-1">Rank</div>
                      <div className="text-sm font-medium text-secondary-900">#{lead.rank}</div>
                    </div>
                  )}
                  {lead.sailing_area && (
                    <div className="text-center p-3 bg-secondary-50 rounded-lg">
                      <div className="text-xs text-secondary-500 mb-1">Sailing Area</div>
                      <div className="text-sm font-medium text-secondary-900">{lead.sailing_area}</div>
                    </div>
                  )}
                  {lead.assigned_to && (
                    <div className="text-center p-3 bg-secondary-50 rounded-lg">
                      <div className="text-xs text-secondary-500 mb-1">Assigned To</div>
                      <div className="text-sm font-medium text-secondary-900">{lead.assigned_to}</div>
                    </div>
                  )}
                </div>

                {/* Industry Codes inline */}
                {(lead.naics_code || lead.sic_code) && (
                  <div className="mt-4 pt-4 border-t border-secondary-100 flex flex-wrap gap-4">
                    {lead.naics_code && (
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-secondary-400" />
                        <span className="text-sm text-secondary-500">NAICS:</span>
                        <span className="text-sm font-mono text-secondary-900">{lead.naics_code}</span>
                      </div>
                    )}
                    {lead.sic_code && (
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-secondary-400" />
                        <span className="text-sm text-secondary-500">SIC:</span>
                        <span className="text-sm font-mono text-secondary-900">{lead.sic_code}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Timestamps inline */}
                <div className="mt-4 pt-4 border-t border-secondary-100 flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="text-secondary-500">Created: </span>
                    <span className="text-secondary-900">{new Date(lead.created_at).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-secondary-500">Updated: </span>
                    <span className="text-secondary-900">{new Date(lead.updated_at).toLocaleDateString()}</span>
                  </div>
                  {lead.source_url && (
                    <a
                      href={lead.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View Source
                    </a>
                  )}
                </div>
              </div>

              <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />

              {/* Company Overview */}
              <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 flex items-center gap-2 mb-4">
                  <Building className="w-5 h-5 text-secondary-500" />
                  Company Overview
                </h3>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lead.year_founded && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Founded</div>
                        <div className="font-medium text-secondary-900">{lead.year_founded}</div>
                      </div>
                    </div>
                  )}

                  {(lead.employees || lead.employee_range) && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <Users className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Employees</div>
                        <div className="font-medium text-secondary-900">
                          {lead.employee_range || lead.employees?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {(lead.revenue || lead.revenue_range) && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <DollarSign className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Revenue</div>
                        <div className="font-medium text-secondary-900">
                          {lead.revenue_range || formatRevenue(lead.revenue)}
                        </div>
                      </div>
                    </div>
                  )}

                  {lead.industry && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <Briefcase className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Industry</div>
                        <div className="font-medium text-secondary-900 text-sm">{lead.industry}</div>
                      </div>
                    </div>
                  )}

                  {lead.sub_industry && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <Tag className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Sub-Industry</div>
                        <div className="font-medium text-secondary-900 text-sm">{lead.sub_industry}</div>
                      </div>
                    </div>
                  )}

                  {lead.parent_company && (
                    <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                      <Building2 className="w-5 h-5 text-secondary-400" />
                      <div>
                        <div className="text-xs text-secondary-500">Parent Company</div>
                        <div className="font-medium text-secondary-900 text-sm">{lead.parent_company}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {lead.tags && lead.tags.filter(Boolean).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-secondary-100">
                    <div className="flex flex-wrap gap-2">
                      {lead.tags.filter(Boolean).map((tag, idx) => (
                        <span key={`${tag}-${idx}`} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact & Social */}
              <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-secondary-500" />
                  Contact Information
                </h3>

                {isEditMode ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">Website</label>
                      <input
                        type="text"
                        value={editForm.website}
                        onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">Phone</label>
                      <input
                        type="text"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="contact@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">Fax</label>
                      <input
                        type="text"
                        value={editForm.fax}
                        onChange={(e) => setEditForm({ ...editForm, fax: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="+1 234 567 8901"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">LinkedIn</label>
                      <input
                        type="text"
                        value={editForm.linkedin_url}
                        onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://linkedin.com/company/..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-secondary-500 mb-1">Facebook</label>
                      <input
                        type="text"
                        value={editForm.facebook_url}
                        onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value })}
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {lead.website && (
                        <a
                          href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
                        >
                          <Globe className="w-5 h-5 text-primary-500" />
                          <span className="text-sm text-primary-600 truncate flex-1">{lead.website.replace(/^https?:\/\//, '')}</span>
                          <ExternalLink className="w-4 h-4 text-secondary-400" />
                        </a>
                      )}

                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
                          <Phone className="w-5 h-5 text-green-500" />
                          <span className="text-sm text-secondary-700">{lead.phone}</span>
                        </a>
                      )}

                      {lead.fax && (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200">
                          <Printer className="w-5 h-5 text-secondary-400" />
                          <div>
                            <div className="text-xs text-secondary-500">Fax</div>
                            <span className="text-sm text-secondary-700">{lead.fax}</span>
                          </div>
                        </div>
                      )}

                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors">
                          <Mail className="w-5 h-5 text-blue-500" />
                          <span className="text-sm text-secondary-700 truncate">{lead.email}</span>
                        </a>
                      )}
                    </div>

                    {/* Social Links */}
                    {(lead.linkedin_url || lead.facebook_url || lead.twitter_url) && (
                      <div className="mt-4 pt-4 border-t border-secondary-100 space-y-2">
                        {lead.linkedin_url && (
                          <a
                            href={lead.linkedin_url.startsWith('http') ? lead.linkedin_url : `https://${lead.linkedin_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
                          >
                            <Linkedin className="w-5 h-5 text-[#0077B5]" />
                            <span className="text-sm text-primary-600 truncate flex-1">
                              {lead.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                            </span>
                            <ExternalLink className="w-4 h-4 text-secondary-400" />
                          </a>
                        )}
                        {lead.facebook_url && (
                          <a
                            href={lead.facebook_url.startsWith('http') ? lead.facebook_url : `https://${lead.facebook_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
                          >
                            <Facebook className="w-5 h-5 text-[#1877F2]" />
                            <span className="text-sm text-primary-600 truncate flex-1">
                              {lead.facebook_url.replace(/^https?:\/\/(www\.)?/, '')}
                            </span>
                            <ExternalLink className="w-4 h-4 text-secondary-400" />
                          </a>
                        )}
                        {lead.twitter_url && (
                          <a
                            href={lead.twitter_url.startsWith('http') ? lead.twitter_url : `https://${lead.twitter_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-lg border border-secondary-200 hover:bg-secondary-50 transition-colors"
                          >
                            <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                            <span className="text-sm text-primary-600 truncate flex-1">
                              {lead.twitter_url.replace(/^https?:\/\/(www\.)?/, '')}
                            </span>
                            <ExternalLink className="w-4 h-4 text-secondary-400" />
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Address & Location */}
              {(lead.address || (lead.latitude && lead.longitude) || isEditMode) && (
                <div className="bg-white rounded-xl border border-secondary-200 overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <h3 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-secondary-500" />
                      Location
                    </h3>

                    {isEditMode ? (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-secondary-500 mb-1">Address</label>
                          <input
                            type="text"
                            value={editForm.address}
                            onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Street address"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-secondary-500 mb-1">City</label>
                          <input
                            type="text"
                            value={editForm.city}
                            onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-secondary-500 mb-1">State/Region</label>
                          <input
                            type="text"
                            value={editForm.state}
                            onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="State or region"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-secondary-500 mb-1">Zip/Postal Code</label>
                          <input
                            type="text"
                            value={editForm.zip_code}
                            onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Zip code"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-secondary-500 mb-1">Country</label>
                          <input
                            type="text"
                            value={editForm.country}
                            onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Country"
                          />
                        </div>
                      </div>
                    ) : lead.address ? (
                      <div className="mb-4">
                        <p className="text-secondary-700">{lead.address}</p>
                        <p className="text-secondary-500 text-sm mt-1">
                          {[lead.city, lead.state, lead.zip_code, lead.country].filter(Boolean).join(', ')}
                        </p>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([lead.name, lead.address, lead.city, lead.state, lead.country].filter(Boolean).join(', '))}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-2 text-sm text-primary-600 hover:text-primary-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open in Google Maps
                        </a>
                      </div>
                    ) : null}
                  </div>

                  {!isEditMode && lead.latitude && lead.longitude && (
                    <div className="h-48 bg-secondary-100">
                      <iframe
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lead.longitude - 0.05},${lead.latitude - 0.05},${lead.longitude + 0.05},${lead.latitude + 0.05}&layer=mapnik&marker=${lead.latitude},${lead.longitude}`}
                        className="w-full h-full border-0"
                        title="Location Map"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Specifications */}
              {(lead.max_length || lead.max_width || lead.max_draft || lead.max_load) && (
                <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                  <h3 className="font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-secondary-500" />
                    Dock Specifications
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {lead.max_length && (
                      <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                        <div className="text-2xl font-bold text-blue-700">{lead.max_length}m</div>
                        <div className="text-xs text-blue-600 mt-1">Max Length</div>
                      </div>
                    )}
                    {lead.max_width && (
                      <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                        <div className="text-2xl font-bold text-green-700">{lead.max_width}m</div>
                        <div className="text-xs text-green-600 mt-1">Max Width</div>
                      </div>
                  )}
                  {lead.max_load && (
                    <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl">
                      <div className="text-2xl font-bold text-amber-700">{lead.max_load}t</div>
                      <div className="text-xs text-amber-600 mt-1">Max Load</div>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Services & Certificates */}
              {((lead.services && lead.services.length > 0) || (lead.certificates && lead.certificates.length > 0)) && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {lead.services && lead.services.filter(Boolean).length > 0 && (
                    <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                      <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                        <Ship className="w-5 h-5 text-secondary-500" />
                        Services
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {lead.services.filter(Boolean).map((service, idx) => (
                          <span key={`${service}-${idx}`} className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {lead.certificates && lead.certificates.filter(Boolean).length > 0 && (
                    <div className="bg-white rounded-xl border border-secondary-200 p-4 sm:p-6">
                      <h3 className="font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-secondary-500" />
                        Certificates
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {lead.certificates.filter(Boolean).map((cert, idx) => (
                          <span key={`${cert}-${idx}`} className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Slide Panel */}
        <div
          className={cn(
            'fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white border-l border-secondary-200 shadow-xl z-50 transition-transform duration-300 overflow-hidden flex flex-col',
            isPanelOpen ? 'translate-x-0' : 'translate-x-full'
          )}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200 bg-secondary-50">
            <h3 className="font-semibold text-secondary-900">Actions & History</h3>
            <button
              onClick={() => setIsPanelOpen(false)}
              className="p-1.5 hover:bg-secondary-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-secondary-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-secondary-200 overflow-x-auto">
            {[
              { key: 'notes', label: 'Notes', icon: MessageSquare, count: notes.length },
              { key: 'activities', label: 'Activities', icon: Clock, count: activities.length },
              { key: 'contacts', label: 'Contacts', icon: Users, count: contacts.length },
              { key: 'files', label: 'Files', icon: Paperclip, count: attachments.length },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-600 hover:text-secondary-900'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-secondary-100 text-secondary-600 text-xs px-1.5 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={3}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || addNote.isPending}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    Add Note
                  </button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-center text-secondary-500 py-8">No notes yet</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div key={note.id} className="p-3 bg-secondary-50 rounded-lg group relative">
                        <p className="text-sm text-secondary-700 whitespace-pre-wrap">{note.content}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2 text-xs text-secondary-400">
                            {note.created_by && (
                              <span className="flex items-center gap-1 text-secondary-600 font-medium">
                                <User className="w-3 h-3" />
                                {note.created_by}
                              </span>
                            )}
                            <span>{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                          <button
                            onClick={() => deleteNote.mutate({ id: note.id, lead_id: lead.id })}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Activities Tab */}
            {activeTab === 'activities' && (
              <div className="space-y-4">
                <button onClick={() => setShowAddActivity(true)} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                  <Plus className="w-4 h-4" />
                  Log Activity
                </button>
                {showAddActivity && (
                  <div className="p-4 border border-secondary-200 rounded-lg space-y-3">
                    <select
                      value={newActivity.type}
                      onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value as LeadActivity['type'] })}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    >
                      <option value="call">Call</option>
                      <option value="email">Email</option>
                      <option value="meeting">Meeting</option>
                      <option value="note">Note</option>
                      <option value="other">Other</option>
                    </select>
                    <input
                      type="text"
                      value={newActivity.title}
                      onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                      placeholder="Title"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    />
                    <textarea
                      value={newActivity.description}
                      onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                      placeholder="Description (optional)"
                      rows={2}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowAddActivity(false)} className="px-3 py-1.5 text-sm text-secondary-600">
                        Cancel
                      </button>
                      <button
                        onClick={handleAddActivity}
                        disabled={!newActivity.title.trim()}
                        className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
                {activities.length === 0 ? (
                  <p className="text-center text-secondary-500 py-8">No activities logged</p>
                ) : (
                  <div className="space-y-3">
                    {activities.map((activity) => {
                      const Icon = ACTIVITY_ICONS[activity.type]
                      return (
                        <div key={activity.id} className="flex gap-3 p-3 bg-secondary-50 rounded-lg">
                          <div className="p-2 bg-white rounded-lg shadow-sm">
                            <Icon className="w-4 h-4 text-secondary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-secondary-900">{activity.title}</p>
                            {activity.description && <p className="text-sm text-secondary-600 mt-1">{activity.description}</p>}
                            <div className="flex items-center gap-2 text-xs text-secondary-400 mt-1">
                              {activity.created_by && (
                                <>
                                  <span className="flex items-center gap-1 text-secondary-600 font-medium">
                                    <User className="w-3 h-3" />
                                    {activity.created_by}
                                  </span>
                                  <span>-</span>
                                </>
                              )}
                              <span>{new Date(activity.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-4">
                <button onClick={() => setShowAddContact(true)} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                  <Plus className="w-4 h-4" />
                  Add Contact
                </button>
                {showAddContact && (
                  <div className="p-4 border border-secondary-200 rounded-lg space-y-3">
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="Name *"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      value={newContact.title}
                      onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                      placeholder="Title"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    />
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      placeholder="Email"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    />
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="Phone"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm"
                    />
                    <label className="flex items-center gap-2 text-sm text-secondary-700">
                      <input
                        type="checkbox"
                        checked={newContact.is_primary}
                        onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                        className="rounded border-secondary-300"
                      />
                      Primary contact
                    </label>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowAddContact(false)} className="px-3 py-1.5 text-sm text-secondary-600">
                        Cancel
                      </button>
                      <button
                        onClick={handleAddContact}
                        disabled={!newContact.name.trim()}
                        className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
                {contacts.length === 0 ? (
                  <p className="text-center text-secondary-500 py-8">No contacts added</p>
                ) : (
                  <div className="space-y-3">
                    {contacts.map((contact) => (
                      <div key={contact.id} className="flex items-start justify-between p-3 bg-secondary-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-secondary-900">{contact.name}</p>
                            {contact.is_primary && (
                              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">Primary</span>
                            )}
                          </div>
                          {contact.title && <p className="text-xs text-secondary-500 mt-0.5">{contact.title}</p>}
                          <div className="flex flex-col gap-1 mt-2">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="text-xs text-secondary-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        </div>
                        <button onClick={() => deleteContact.mutate({ id: contact.id, lead_id: lead.id })} className="p-1 text-secondary-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Files Tab */}
            {activeTab === 'files' && (
              <div className="space-y-4">
                <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
                  <Upload className="w-4 h-4" />
                  Upload File
                </button>
                <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" />
                {attachments.length === 0 ? (
                  <p className="text-center text-secondary-500 py-8">No files attached</p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-secondary-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <a href={file.file_path} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-secondary-900 hover:text-primary-600 truncate block">
                              {file.file_name}
                            </a>
                            <p className="text-xs text-secondary-500">{(file.file_size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteAttachment.mutate({ id: file.id, lead_id: lead.id, file_path: file.file_path })}
                          className="p-1 text-secondary-400 hover:text-red-500 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Panel Overlay - closes panel when clicking outside */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setIsPanelOpen(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Delete Company</h3>
                  <p className="text-sm text-secondary-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to delete <strong>{lead.name}</strong>? All associated notes, activities, contacts, and files will be permanently removed.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-secondary-700 hover:bg-secondary-100 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteLead}
                  disabled={deleteLead.isPending}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {deleteLead.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
